// =============================================================================
// FORM CANVAS - Professional form editing area like SurveyJS
// =============================================================================

import { useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Plus, Trash2, ChevronDown, ChevronUp, MoreHorizontal, FileText, GripVertical } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { FormFieldCard } from './FormFieldCard';
import { useFormBuilderStore } from '../store';
import { cn } from '../../../lib/utils';
import type { FieldType } from '../types';

// Get width class based on field width
function getFieldWidthClass(width?: string): string {
  switch (width) {
    case 'quarter':
      return 'col-span-3'; // 3 of 12
    case 'third':
      return 'col-span-4'; // 4 of 12
    case 'half':
      return 'col-span-6'; // 6 of 12
    case 'two-thirds':
      return 'col-span-8'; // 8 of 12
    case 'three-quarters':
      return 'col-span-9'; // 9 of 12
    case 'full':
    default:
      return 'col-span-12'; // 12 of 12
  }
}


export function FormCanvas() {
  const {
    sections,
    selectedFieldId,
    selectedSectionId,
    previewMode,
    theme,
    settings,
    addSection,
    updateSection,
    removeSection,
    addField,
    moveField,
    selectField,
    selectSection,
  } = useFormBuilderStore();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over) return;

      const activeId = active.id as string;
      const overId = over.id as string;

      if (activeId === overId) return;

      // Find which sections contain the fields
      let fromSectionId: string | null = null;
      let toSectionId: string | null = null;
      let fromIndex = -1;
      let toIndex = -1;

      for (const section of sections) {
        const activeIndex = section.fields.findIndex((f) => f.id === activeId);
        const overIndex = section.fields.findIndex((f) => f.id === overId);

        if (activeIndex !== -1) {
          fromSectionId = section.id;
          fromIndex = activeIndex;
        }
        if (overIndex !== -1) {
          toSectionId = section.id;
          toIndex = overIndex;
        }
      }

      if (fromSectionId && toSectionId && fromIndex !== -1 && toIndex !== -1) {
        moveField(fromSectionId, toSectionId, fromIndex, toIndex);
      }
    },
    [sections, moveField]
  );

  const handleCanvasClick = () => {
    selectField(null);
    selectSection(null);
  };

  const handleDrop = useCallback(
    (event: React.DragEvent, sectionId: string) => {
      event.preventDefault();
      const fieldType = event.dataTransfer.getData('application/formfield') as FieldType;
      if (fieldType) {
        addField(sectionId, fieldType);
      }
    },
    [addField]
  );

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  // Get font size class based on theme
  const getFontSizeClass = () => {
    switch (theme.fontSize) {
      case 'sm':
        return 'text-sm';
      case 'lg':
        return 'text-lg';
      default:
        return 'text-base';
    }
  };

  // Get border radius class based on theme
  const getBorderRadiusClass = () => {
    switch (theme.borderRadius) {
      case 'none':
        return 'rounded-none';
      case 'sm':
        return 'rounded-sm';
      case 'lg':
        return 'rounded-xl';
      case 'xl':
        return 'rounded-2xl';
      default:
        return 'rounded-lg';
    }
  };

  // Render preview mode
  if (previewMode) {
    return (
      <div
        className="flex-1 overflow-y-auto p-8"
        style={{ backgroundColor: theme.backgroundColor }}
      >
        <div className="max-w-2xl mx-auto">
          {/* Form header */}
          <div className="mb-8 text-center">
            <h1
              className="text-2xl font-bold mb-2"
              style={{ color: theme.textColor }}
            >
              {sections[0]?.title || 'Formulaire'}
            </h1>
            {sections[0]?.description && (
              <p className="text-secondary-500">{sections[0].description}</p>
            )}
          </div>

          {/* Form sections */}
          {sections.map((section, sectionIndex) => (
            <div
              key={section.id}
              className={cn(
                'mb-8 p-6 bg-white',
                settings.layout === 'card' && 'shadow-md',
                settings.layout === 'bordered' && 'border border-secondary-200',
                getBorderRadiusClass()
              )}
            >
              {sectionIndex > 0 && section.title && (
                <h2
                  className="text-lg font-semibold mb-4"
                  style={{ color: theme.textColor }}
                >
                  {section.title}
                </h2>
              )}

              <div className="grid grid-cols-12 gap-4">
                {section.fields.map((field) => (
                  <div
                    key={field.id}
                    className={cn(getFieldWidthClass(field.width), getFontSizeClass())}
                  >
                    {/* Field label */}
                    {!['divider', 'heading', 'paragraph', 'html'].includes(field.type) && (
                      <label
                        className="block font-medium mb-2"
                        style={{ color: theme.textColor }}
                      >
                        {field.label}
                        {settings.showRequiredAsterisk &&
                          field.validation.some((v) => v.type === 'required') && (
                            <span className="text-red-500 ml-1">*</span>
                          )}
                      </label>
                    )}

                    {/* Field preview - simplified for preview mode */}
                    <FormFieldCard
                      field={field}
                      sectionId={section.id}
                      isSelected={false}
                      onClick={() => {}}
                    />

                    {/* Help text */}
                    {field.helpText && (
                      <p className="text-sm text-secondary-500 mt-1">{field.helpText}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Submit buttons */}
          <div className="flex justify-center gap-3 mt-8">
            {settings.resetLabel && (
              <Button variant="outline" size="lg">
                {settings.resetLabel}
              </Button>
            )}
            <Button
              size="lg"
              style={{ backgroundColor: theme.primaryColor }}
            >
              {settings.submitLabel || 'Envoyer'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Render edit mode
  return (
    <div
      className="flex-1 overflow-y-auto p-8 bg-secondary-50 dark:bg-secondary-950"
      onClick={handleCanvasClick}
    >
      <div className="max-w-3xl mx-auto space-y-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          {sections.map((section, index) => (
            <div
              key={section.id}
              onDrop={(e) => handleDrop(e, section.id)}
              onDragOver={handleDragOver}
              className={cn(
                'group rounded-xl border-2 border-transparent transition-all',
                selectedSectionId === section.id
                  ? 'border-primary-500/50'
                  : 'hover:border-secondary-200 dark:hover:border-secondary-700'
              )}
              onClick={(e) => {
                e.stopPropagation();
                selectSection(section.id);
              }}
            >
              <div
                className={cn(
                  'rounded-xl border bg-white p-6',
                  'dark:border-secondary-700 dark:bg-secondary-800',
                  sections.length > 1 && 'border-secondary-200'
                )}
              >
                {/* Section header with controls */}
                <div className="flex items-center gap-3 mb-4">
                  <GripVertical className="h-5 w-5 text-secondary-400 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity" />

                  <Input
                    value={section.title || ''}
                    onChange={(e) => updateSection(section.id, { title: e.target.value })}
                    placeholder="Titre de la section"
                    className="font-semibold text-lg border-0 bg-transparent p-0 h-auto focus-visible:ring-0 placeholder:text-secondary-400"
                  />

                  <div className="flex items-center gap-1 ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                    {index > 0 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Move section up logic
                        }}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                    )}
                    {index < sections.length - 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Move section down logic
                        }}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    )}

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => addSection(section.id)}>
                          <Plus className="h-4 w-4 mr-2" />
                          Ajouter une section après
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => removeSection(section.id)}
                          disabled={sections.length <= 1}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Supprimer la section
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Section description */}
                {selectedSectionId === section.id && (
                  <Input
                    value={section.description || ''}
                    onChange={(e) => updateSection(section.id, { description: e.target.value })}
                    placeholder="Description de la section (optionnel)"
                    className="text-sm text-secondary-500 border-0 bg-transparent p-0 h-auto mb-4 focus-visible:ring-0 placeholder:text-secondary-400"
                  />
                )}

                {/* Fields */}
                <SortableContext
                  items={section.fields.map((f) => f.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className={cn(section.fields.length === 0 && 'min-h-[120px]')}>
                    {section.fields.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-secondary-200 dark:border-secondary-700 rounded-lg">
                        <FileText className="h-10 w-10 text-secondary-300 dark:text-secondary-600 mb-3" />
                        <p className="text-secondary-500 font-medium mb-1">
                          Aucun champ dans cette section
                        </p>
                        <p className="text-secondary-400 text-sm mb-4">
                          Glissez un champ depuis la palette ou cliquez ci-dessous
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            addField(section.id, 'text');
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Ajouter un champ texte
                        </Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-12 gap-3">
                        {section.fields.map((field) => (
                          <div
                            key={field.id}
                            className={getFieldWidthClass(field.width)}
                          >
                            <FormFieldCard
                              field={field}
                              sectionId={section.id}
                              isSelected={selectedFieldId === field.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                selectField(field.id);
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </SortableContext>

                {/* Add field button (only show if section has fields) */}
                {section.fields.length > 0 && (
                  <div className="mt-4 flex justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        addField(section.id, 'text');
                      }}
                      className="text-secondary-500 hover:text-secondary-700"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Ajouter un champ
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </DndContext>

        {/* Add section button */}
        <div className="flex justify-center pt-4">
          <Button
            variant="outline"
            onClick={() => addSection()}
            className="border-dashed border-2 hover:border-primary-500 hover:text-primary-600"
          >
            <Plus className="h-4 w-4 mr-2" />
            Ajouter une section
          </Button>
        </div>
      </div>
    </div>
  );
}
